import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { MdArrowLeft, MdArrowRight } from "react-icons/md";
import { DATE_FORMAT_SERVER } from '../../../../constants';

const WeeklyCalendar = ({
    dateRanges,
    setDateRanges,
}) => {
    const initialMonth = dateRanges.length > 0
        ? DateTime.fromISO(dateRanges[0].start)
        : DateTime.now();

    const [currentMonth, setCurrentMonth] = useState(initialMonth);

    const handlePrevMonth = () => setCurrentMonth(prev => prev.minus({ months: 1 }));
    const handleNextMonth = () => setCurrentMonth(prev => prev.plus({ months: 1 }));

    // Generate all the weeks in the current month (Monday to Sunday)
    const weeksInMonth = [];
    let startOfMonth = currentMonth.startOf('month');
    let endOfMonth = currentMonth.endOf('month');

    // Adjust to the first Monday on or before the first of the month
    let currentWeekStart = startOfMonth.weekday === 1
        ? startOfMonth
        : startOfMonth.minus({ days: (startOfMonth.weekday + 6) % 7 });

    while (currentWeekStart <= endOfMonth) {
        let currentWeekEnd = currentWeekStart.plus({ days: 6 });

        weeksInMonth.push({
            start: currentWeekStart,
            end: currentWeekEnd,
        });

        // Move to the next week
        currentWeekStart = currentWeekStart.plus({ weeks: 1 });
    }

    const isWeekSelected = (weekStart) => {
        const formatted = weekStart.startOf("day").toFormat(DATE_FORMAT_SERVER);
        return dateRanges.some(range => range.start === formatted);
    };

    const toggleWeek = (weekStart, weekEnd) => {
        const start = weekStart.startOf("day").toFormat(DATE_FORMAT_SERVER);
        const end = weekEnd.endOf("day").toFormat(DATE_FORMAT_SERVER);

        const alreadySelected = dateRanges.some(range => range.start === start);

        if (alreadySelected) {
            // Don't allow deselecting the last one
            if (dateRanges.length <= 1) return;
            setDateRanges(dateRanges.filter(range => range.start !== start));
        } else {
            // Add and sort by start date
            const updated = [...dateRanges, { start, end }];
            updated.sort((a, b) => a.start.localeCompare(b.start));
            setDateRanges(updated);
        }
    };

    return (
        <div className='z-[100] absolute top-0 right-0 p-5 bg-white w-[350px] shadow-xl'>
            <div className='flex items-center justify-between'>
                <button
                    onClick={handlePrevMonth}
                    type='button'
                    className='w-[20px] h-[20px] bg-gray-200 rounded-[2px] flex items-center justify-center'
                >
                    <MdArrowLeft className="text-xl flex-shrink-0" />
                </button>
                <p className='text-sm font-bold'>
                    {currentMonth.toFormat('MMMM yyyy')}
                </p>
                <button
                    onClick={handleNextMonth}
                    type='button'
                    className='w-[20px] h-[20px] bg-gray-200 rounded-[2px] flex items-center justify-center'
                >
                    <MdArrowRight className="text-xl flex-shrink-0" />
                </button>
            </div>

            {/* Week List */}
            <div className='mt-3'>
                {weeksInMonth.map((week, index) => {
                    const daysInWeek = Array.from({ length: 7 }, (_, i) =>
                        week.start.plus({ days: i })
                    );

                    const isSelected = isWeekSelected(week.start);

                    return (
                        <button
                            key={index}
                            onClick={() => toggleWeek(week.start, week.end)}
                            type="button"
                            className={`w-full mb-2 p-3 rounded-lg ${isSelected ? "bg-primary text-white" : "bg-gray-100"} hover:bg-primary hover:text-white`}
                        >
                            <div className="font-semibold mb-1">Week {week.start.weekNumber}</div>
                            <div className="flex justify-between text-xs">
                                {daysInWeek.map((day, i) => (
                                    <div key={i} className="flex-1 text-center">
                                        <div>{day.toFormat('d')}</div>
                                        <div>{day.toFormat('ccc')}</div>
                                    </div>
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Selected badges */}
            <div className='mt-1 flex flex-wrap gap-1 max-h-[80px] overflow-y-auto'>
                {dateRanges.map((range) => {
                    const start = DateTime.fromISO(range.start);
                    const label = `W${start.weekNumber} ${start.toFormat('MMM d')}`;
                    return (
                        <span
                            key={range.start}
                            className='inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-medium'
                        >
                            {label}
                            {dateRanges.length > 1 && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDateRanges(dateRanges.filter(r => r.start !== range.start));
                                    }}
                                    className='hover:text-red-500 font-bold text-[10px] leading-none'
                                >
                                    ✕
                                </button>
                            )}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default WeeklyCalendar;
